import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { CircleNotch } from "@phosphor-icons/react";
import { api } from "../lib/api";
import TopBar from "../components/TopBar";
import KpiStrip from "../components/KpiStrip";
import MarketBreadthMeter from "../components/MarketBreadthMeter";
import StockDetailModal from "../components/StockDetailModal";
import SectorScreener from "../components/SectorScreener";
import SectorMovers from "../components/SectorMovers";
import SectorHeatmap from "../components/SectorHeatmap";
import FnoOI from "../components/FnoOI";
import TopGainers from "../components/TopGainers";
import FilterBar from "../components/dashboard/FilterBar";
import DashboardSidebar from "../components/dashboard/DashboardSidebar";
import { ColumnSection } from "../components/dashboard/ColumnSection";
import { ClosedView, WatchlistView, EmptyState } from "../components/dashboard/Views";
import RetestView from "../components/dashboard/RetestView";
import CupHandleView from "../components/dashboard/CupHandleView";
import ApproachingView from "../components/dashboard/ApproachingView";
import StrongestView from "../components/dashboard/StrongestView";
import PowerBreakoutsView from "../components/dashboard/PowerBreakoutsView";
import EmaCrossoverView from "../components/dashboard/EmaCrossoverView";
import GoldenCrossView from "../components/dashboard/GoldenCrossView";
import InstitutionalView from "../components/dashboard/InstitutionalView";
import { FreePreviewBanner, SignalsSubTabs } from "../components/dashboard/SignalsHeader";
import OnboardingTour from "../components/OnboardingTour";
import TelegramCTA from "../components/TelegramCTA";
import ViewsWidget from "../components/ViewsWidget";
import { useAuth } from "../contexts/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [grouped, setGrouped] = useState({ short_term: [], long_term: [], fresh_today: [], closed: [], ist_today: "" });
  const [watchlist, setWatchlist] = useState([]);
  const [scanStatus, setScanStatus] = useState(null);
  const [search, setSearch] = useState("");
  const [minVol, setMinVol] = useState(2);
  const [sort, setSort] = useState("volume");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("signals"); // signals | gainers | sectors | fno | watchlist
  const [signalsView, setSignalsView] = useState("active"); // active | fresh | closed
  const [tourReady, setTourReady] = useState(false);
  const [tourReplayKey, setTourReplayKey] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile sidebar toggle

  const loadAll = useCallback(async () => {
    try {
      const [g, s, st, w] = await Promise.all([
        api.get("/signals/grouped"),
        api.get("/stats"),
        api.get("/scan/status"),
        api.get("/watchlist"),
      ]);
      setGrouped(g.data);
      setStats(s.data);
      setScanStatus(st.data);
      setWatchlist(w.data);
    } catch (e) {
      console.error("loadAll", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Onboarding tour: trigger once after data loads if not yet completed.
  // The tour itself reads `localStorage["bs.onboarding.v2"]` to decide.
  useEffect(() => {
    if (loading) return;
    if (!grouped?.short_term?.length && !grouped?.long_term?.length) return;
    // small delay so UI settles before highlighting
    const id = setTimeout(() => setTourReady(true), 600);
    return () => clearTimeout(id);
  }, [loading, grouped]);

  // Manual "Take Tour" button (TopBar) writes to bs.tour.replay → re-arm the
  // tour regardless of completion flag.
  useEffect(() => {
    const onReplay = (e) => {
      if (e.key && e.key !== "bs.tour.replay") return;
      setTourReady(false);
      // Force a fresh mount of OnboardingTour to skip the "done" guard
      setTourReplayKey((k) => k + 1);
      setTimeout(() => setTourReady(true), 200);
    };
    window.addEventListener("storage", onReplay);
    // Also poll for same-tab trigger (storage event doesn't fire in same tab)
    let last = localStorage.getItem("bs.tour.replay");
    const id = setInterval(() => {
      const cur = localStorage.getItem("bs.tour.replay");
      if (cur && cur !== last) {
        last = cur;
        onReplay({ key: "bs.tour.replay" });
      }
    }, 700);
    return () => {
      window.removeEventListener("storage", onReplay);
      clearInterval(id);
    };
  }, []);

  // Refresh watchlist live P&L every 60s while Watchlist tab is open
  useEffect(() => {
    if (tab !== "watchlist") return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/watchlist");
        setWatchlist(data);
      } catch (e) {
        console.error("watchlist refresh", e);
      }
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [tab]);

  // Poll scan status periodically — regardless of running state.
  // (Previously only polled while a scan was actively running, so the UI's
  // "Last Scan" timestamp went stale once a background scheduler finished
  // a scan while the user had the dashboard open. Now refreshes every 60s.)
  useEffect(() => {
    const fastInterval = 5000;
    const slowInterval = 60000;
    const tick = async () => {
      try {
        const [st, s, g] = await Promise.all([
          api.get("/scan/status"),
          api.get("/stats"),
          api.get("/signals/grouped"),
        ]);
        setScanStatus(st.data);
        setStats(s.data);
        setGrouped(g.data);
      } catch (e) {
        console.error("scan poll", e);
      }
    };
    // Use fast (5s) interval while a scan is actively running, slow (60s)
    // otherwise. This keeps the UI fresh without hammering the API.
    const interval = scanStatus?.running ? fastInterval : slowInterval;
    const id = setInterval(tick, interval);
    return () => clearInterval(id);
  }, [scanStatus?.running]);

  const runScan = async (opts = {}) => {
    try {
      // Manual "Run Scan" button defaults to NIFTY 500 universe (~500 stocks)
      // instead of full NSE universe (~2,449 stocks). Reason: production
      // container OOMs on the full scan on demand. The daily 16:00 IST cron
      // still runs the full universe (`total`) at off-hours when traffic is
      // low. ~99% of premium breakouts come from NIFTY 500 anyway.
      const params = [];
      params.push("universe=nifty500");
      if (opts.force) params.push("force=true");
      const url = `/scan/run?${params.join("&")}`;
      const { data } = await api.post(url);
      setScanStatus((s) => ({ ...(s || {}), running: true }));
      if (data?.message) {
        // surface server's "scan started" / "already running" message
        // eslint-disable-next-line no-alert
        alert(data.message);
      }
    } catch (e) {
      console.error("scan/run failed:", e);
      const raw = e?.response?.data?.detail;
      const detail =
        (raw && typeof raw === "object" && raw.message) ||
        (typeof raw === "string" && raw) ||
        e?.response?.data?.message ||
        e?.message ||
        "Scan refresh failed. Please try again in a few minutes.";
      const retryAfter = (raw && typeof raw === "object" && raw.retry_after_seconds) || e?.response?.data?.retry_after_seconds;
      // Admin-only: if server says "another scanner is running" and offers
      // a force-bypass hint, ask the admin once if they want to override
      // and force-run the breakout scan now.
      const forceHint = raw && typeof raw === "object" && raw.admin_force_hint;
      if (forceHint && user?.role === "admin" && !opts.force) {
        // eslint-disable-next-line no-alert
        const ok = window.confirm(
          `${detail}\n\nForce-run the breakout scan now? (Admin override — releases the stuck lock.)`
        );
        if (ok) {
          await runScan({ force: true });
          return;
        }
      }
      const msg = retryAfter
        ? `${detail}\n\nTry again in ${Math.ceil(retryAfter / 60)} minutes.`
        : detail;
      // eslint-disable-next-line no-alert
      alert(msg);
    }
  };

  const filterSort = useCallback(
    (list) => {
      const q = search.trim().toLowerCase();
      // When searching, bypass the min-volume filter so user always finds their
      // symbol — volume threshold is for browsing, not targeted lookups.
      let out = q
        ? list.filter(
            (s) =>
              s.symbol.toLowerCase().includes(q) ||
              (s.company_name || "").toLowerCase().includes(q),
          )
        : list.filter((s) => s.volume_multiple >= minVol);
      if (sort === "strength") {
        out = [...out].sort((a, b) => {
          if (a.strength === b.strength) return b.volume_multiple - a.volume_multiple;
          return a.strength === "Strong" ? -1 : 1;
        });
      } else if (sort === "recent") {
        out = [...out].sort((a, b) => (a.scanned_at < b.scanned_at ? 1 : -1));
      } else if (sort === "pct_change") {
        out = [...out].sort(
          (a, b) => (b.pct_change_today ?? -1e9) - (a.pct_change_today ?? -1e9),
        );
      } else {
        out = [...out].sort((a, b) => b.volume_multiple - a.volume_multiple);
      }
      return out;
    },
    [minVol, search, sort],
  );

  const short = useMemo(() => filterSort(grouped.short_term), [grouped.short_term, filterSort]);
  const long_ = useMemo(() => filterSort(grouped.long_term), [grouped.long_term, filterSort]);
  const freshSet = useMemo(
    () => new Set((grouped.fresh_today || []).map((s) => `${s.symbol}:${s.signal_type}`)),
    [grouped.fresh_today],
  );
  const freshShort = useMemo(() => short.filter((s) => freshSet.has(`${s.symbol}:${s.signal_type}`)), [short, freshSet]);
  const freshLong = useMemo(() => long_.filter((s) => freshSet.has(`${s.symbol}:${s.signal_type}`)), [long_, freshSet]);
  const closed = useMemo(() => (grouped.closed || []).filter((s) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return s.symbol.toLowerCase().includes(q) || (s.company_name || "").toLowerCase().includes(q);
  }), [grouped.closed, search]);

  const watchedSet = useMemo(() => new Set(watchlist.map((w) => w.symbol)), [watchlist]);

  const reloadWatchlist = useCallback(async () => {
    try {
      const w = await api.get("/watchlist");
      setWatchlist(w.data);
    } catch (e) {
      console.error("reloadWatchlist", e);
    }
  }, []);

  const toggleWatch = async (signal) => {
    if (watchedSet.has(signal.symbol)) {
      await api.delete(`/watchlist/${signal.symbol}`);
    } else {
      await api.post("/watchlist", { symbol: signal.symbol });
    }
    const w = await api.get("/watchlist");
    setWatchlist(w.data);
  };

  return (
    <div className="min-h-screen bg-app grain" data-testid="dashboard">
      <TopBar onRunScan={runScan} scanning={scanStatus?.running} lastScan={scanStatus?.last_run} />
      <KpiStrip stats={stats} lastScan={scanStatus?.last_run} />
      <MarketBreadthMeter />

      <div className="flex flex-col lg:flex-row lg:items-stretch">
        <DashboardSidebar
          tab={tab}
          setTab={setTab}
          watchlistCount={watchlist.length}
          isAdmin={user?.role === "admin"}
          mobileOpen={sidebarOpen}
          onMobileToggle={() => setSidebarOpen((o) => !o)}
        />

        <div className="flex-1 min-w-0">
          <FilterBar
            search={search} setSearch={setSearch}
            minVol={minVol} setMinVol={setMinVol}
            sort={sort} setSort={setSort}
            visible={tab === "signals" || tab === "watchlist"}
            onOpenSymbol={(s) => setSelected(s)}
          />

          <div className="p-6">
        {scanStatus?.running && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 border border-subtle bg-surface rounded-sm flex items-center gap-3"
          >
            <CircleNotch size={16} className="animate-spin text-pos" weight="bold" />
            <div className="text-sm text-secondary-fg">
              Scanning NSE universe… results will stream in as breakouts are detected.
            </div>
          </motion.div>
        )}

        {tab === "signals" ? (
          <>
            <FreePreviewBanner grouped={grouped} />
            {search.trim() && (
              <div
                data-testid="search-summary"
                className="mb-4 p-3 border border-mod-soft bg-mod-soft rounded-sm flex flex-wrap items-center gap-3 text-sm"
              >
                <span className="text-mod font-mono text-[10px] uppercase tracking-widest">
                  /// Searching "{search.trim()}"
                </span>
                <span className="text-primary-fg">
                  {short.length + long_.length} active match
                  {(short.length + long_.length) !== 1 ? "es" : ""}
                  <span className="text-muted-fg"> ({short.length} short-term · {long_.length} long-term · {closed.length} closed)</span>
                </span>
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  data-testid="clear-search"
                  className="ml-auto px-2 py-1 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:bg-surface-hover"
                >
                  Clear
                </button>
              </div>
            )}
            <SignalsSubTabs
              signalsView={signalsView}
              setSignalsView={setSignalsView}
              counts={{
                active: short.length + long_.length,
                fresh: freshShort.length + freshLong.length,
                closed: closed.length,
              }}
              istToday={grouped.ist_today}
            />

            {signalsView === "closed" ? (
              <ClosedView items={closed} onOpen={setSelected} />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ColumnSection
                  title="Short-Term Breakouts"
                  subtitle={signalsView === "fresh" ? "New 6-month highs · last 3 trading days" : "New 6-month highs · active"}
                  items={signalsView === "fresh" ? freshShort : short}
                  loading={loading}
                  onOpen={setSelected}
                  onWatch={toggleWatch}
                  watchedSet={watchedSet}
                  freshSet={freshSet}
                  testid="column-short"
                />
                <ColumnSection
                  title="Long-Term Breakouts"
                  subtitle={signalsView === "fresh" ? "New 2-year highs · last 3 trading days" : "New 2-year highs · active"}
                  items={signalsView === "fresh" ? freshLong : long_}
                  loading={loading}
                  onOpen={setSelected}
                  onWatch={toggleWatch}
                  watchedSet={watchedSet}
                  freshSet={freshSet}
                  testid="column-long"
                />
              </div>
            )}
          </>
        ) : tab === "sectors" ? (
          <>
            <SectorHeatmap />
            <SectorScreener />
            <SectorMovers />
          </>
        ) : tab === "gainers" ? (
          <TopGainers />
        ) : tab === "fno" ? (
          <FnoOI onOpenStock={setSelected} />
        ) : tab === "retest" ? (
          <RetestView onWatch={toggleWatch} watchedSet={watchedSet} onOpenStock={setSelected} />
        ) : tab === "cuphandle" ? (
          <CupHandleView onWatch={toggleWatch} watchedSet={watchedSet} onOpenStock={setSelected} />
        ) : tab === "approaching" ? (
          <ApproachingView onWatch={toggleWatch} watchedSet={watchedSet} onOpenStock={setSelected} />
        ) : tab === "ema" ? (
          <EmaCrossoverView onWatch={toggleWatch} watchedSet={watchedSet} onOpenStock={setSelected} />
        ) : tab === "goldencross" ? (
          <GoldenCrossView onWatch={toggleWatch} watchedSet={watchedSet} onOpenStock={setSelected} />
        ) : tab === "institutional" ? (
          <InstitutionalView onWatch={toggleWatch} watchedSet={watchedSet} onOpenStock={setSelected} />
        ) : tab === "strongest" ? (
          <StrongestView onWatch={toggleWatch} watchedSet={watchedSet} onOpenStock={setSelected} />
        ) : tab === "power" ? (
          <PowerBreakoutsView onWatch={toggleWatch} watchedSet={watchedSet} onOpenStock={setSelected} />
        ) : (
          <WatchlistView items={watchlist} onOpen={setSelected} onWatch={toggleWatch} watchedSet={watchedSet} onReload={reloadWatchlist} />
        )}

        {!scanStatus?.running && !loading && short.length === 0 && long_.length === 0 && tab === "signals" && (
          <EmptyState onRun={runScan} lastScan={scanStatus?.last_run} />
        )}
          </div>
        </div>
      </div>

      <StockDetailModal
        signal={selected}
        onClose={() => setSelected(null)}
        onWatch={toggleWatch}
        watched={selected ? watchedSet.has(selected.symbol) : false}
        onReloadWatchlist={reloadWatchlist}
        onUpdated={(updated) => {
          // Patch the in-memory grouped lists so card + modal stay in sync
          // without a full network round-trip; also refresh in background
          // to pick up server-side derived fields.
          setGrouped((g) => {
            const patch = (arr) =>
              arr.map((s) =>
                s.symbol === updated.symbol &&
                s.signal_type === updated.signal_type &&
                s.first_detected_at === updated.first_detected_at
                  ? { ...s, ...updated }
                  : s,
              );
            return {
              ...g,
              short_term: patch(g.short_term || []),
              long_term: patch(g.long_term || []),
              fresh_today: patch(g.fresh_today || []),
              closed: patch(g.closed || []),
            };
          });
          setSelected((s) => (s ? { ...s, ...updated } : s));
          loadAll();
        }}
      />

      <OnboardingTour key={tourReplayKey} shouldStart={tourReady} forceStart={tourReplayKey > 0} onClose={() => setTourReady(false)} />

      {/* SEBI compliance banner — visible on every dashboard view */}
      <div
        className="mt-10 mx-6 px-4 py-3 border border-mod/40 bg-mod-soft/30 rounded-sm text-[11px] font-mono leading-relaxed text-mod"
        data-testid="dash-sebi-disclaimer"
      >
        <div className="font-bold uppercase tracking-widest mb-1">⚠️ Important disclosure</div>
        <span className="normal-case text-secondary-fg">
          Breakout Scanner is an <b>educational/research tool</b>. We are{" "}
          <b className="text-mod">NOT a SEBI-registered Research Analyst</b> or
          Investment Advisor. All signals, scores, and trade plans shown here
          are <b>technical observations</b>, not investment recommendations.
          Trading in equities/derivatives involves substantial risk of capital
          loss. Past performance does not guarantee future results.{" "}
          <b>Consult your SEBI-registered financial advisor</b> before any trade.
        </span>
      </div>

      <footer className="mt-4 px-6 py-6 border-t border-subtle text-[10px] font-mono uppercase tracking-widest text-muted-fg flex flex-wrap items-center gap-4">
        <span>© Breakout Scanner · Educational research tool · Not SEBI-registered</span>
        <span className="text-primary-fg/30">|</span>
        <a href="/terms" target="_blank" rel="noreferrer" className="hover:text-primary-fg" data-testid="dash-footer-terms">Terms</a>
        <a href="/refund" target="_blank" rel="noreferrer" className="hover:text-primary-fg" data-testid="dash-footer-refund">Refund Policy</a>
        <a href="/privacy" target="_blank" rel="noreferrer" className="hover:text-primary-fg" data-testid="dash-footer-privacy">Privacy</a>
        <a href="/about" target="_blank" rel="noreferrer" className="hover:text-primary-fg" data-testid="dash-footer-about">About</a>
        <a href="/backtest" target="_blank" rel="noreferrer" className="hover:text-primary-fg" data-testid="dash-footer-backtest">Backtest</a>
        <TelegramCTA variant="footer" testId="dash-footer-telegram" />
        {user?.role === "admin" && (
          <>
            <span className="text-primary-fg/30">|</span>
            <ViewsWidget variant="footer" />
          </>
        )}
        <a href="/pricing" className="hover:text-primary-fg ml-auto">Manage subscription</a>
      </footer>
    </div>
  );
}
